import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  FormControl,
  List,
  TextField,
  Divider,
} from "@mui/material";
import localize from "../../../common/localize";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import Combobox from "../../../components/common/Combobox";
import useGlobalContext from "../../../hooks/useGlobalContext";
import FilterBox from "../../../components/common/FilterBox";
import { Add } from "@mui/icons-material";
import { MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION } from "../../../common/utils";
import { getClearInventoryMovement } from "../../../common/EntitiesManagement";
import ModalBackground from "../../../components/common/ModalBackground";
import AddInventoryMovement from "../../inventory/InventoryMovement/AddInventoryMovement";
import LoadingPanel from "../../../components/common/LoadingPanel";
import InventoryMovementItem from "../../inventory/InventoryMovement/InventoryMovementItem";
import EvidenceTempleateItem from "../../evidenceTemplate/components/EvidenceTemplateItem";
import InventoryMovementFilter from "../../inventory/InventoryMovement/InventoryMovementFilter";
import ExpandableView from "../../../components/common/ExpandableView";
import CreatedByText from "../../../components/common/CreatedByText";

export default function AddOrEditSupply({
  model,
  setModel,
  readOnly,
  isWarehouseView,
  warehouse,
}) {
  const {
    MEASUREMENTUNIT_API,
    PROVIDER_ENDPOINT,
    config,
    WAREHOUSE_ENDPOINT,
    INVENTORY_MOVEMENT_HEADER_ENDPOINT,
  } = useGlobalContext();
  const [descriptionCount, setDescriptionCount] = useState(0);
  const [measurementUnitData, setMeasurementUnitData] = useState(null);
  const [providersData, setPRovidersData] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTab, setSelectedtab] = useState("0");
  const [newTag, setNewTag] = useState("");
  const [inventoryMovementHeaderData, setInventoryMovementHeaderData] =
    useState([]);
  const [inventoryMovementHeaderModel, setInventoryMovementHeaderModel] =
    useState();
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingInventoryMovements, setLoadingInventoryMovements] =
    useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [warehousesData, setWarehousesData] = useState([]);
  const [warehousesFiltered, setWarehousesFiltered] = useState([]);
  const [warehouseFilter, setWarehouseFilter] = useState();
  const [inventoryMovementHeaderFiltered, setInventoryMovementHeaderFiltered] =
    useState([]);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;

  const getMeasurementUnits = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${MEASUREMENTUNIT_API}/`, requestConfig);

    setMeasurementUnitData(data?.data);
  };

  const getProviders = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${PROVIDER_ENDPOINT}/`, requestConfig);

    setPRovidersData(data?.data);
  };

  const getInventoryMovementHeaders = async () => {
    try {
      if (model?.Id) {
        setLoadingInventoryMovements(true);
        const requestConfig = {
          ...config,
        };
        requestConfig.headers.pagination = -1;
        const url = isWarehouseView
          ? `${INVENTORY_MOVEMENT_HEADER_ENDPOINT}/supply/${model?.Id}/warehouse/${warehouse.Id}`
          : `${INVENTORY_MOVEMENT_HEADER_ENDPOINT}/supply/${model?.Id}`;
        const data = await axios.get(url, requestConfig);

        setInventoryMovementHeaderData(data?.data);
        setInventoryMovementHeaderFiltered(data?.data?.Data);
      }
    } catch {
      setInventoryMovementHeaderData(undefined);
    } finally {
      setLoadingInventoryMovements(false);
    }
  };

  const getWarehouses = async () => {
    try {
      if (model?.Id) {
        setLoadingWarehouses(true);
        const requestConfig = {
          ...config,
        };
        requestConfig.headers.pagination = -1;
        const data = await axios.get(
          `${WAREHOUSE_ENDPOINT}/supply/${model?.Id}`,
          requestConfig
        );

        setWarehousesData(data?.data);
        setWarehousesFiltered(data?.data?.Data);
      }
    } catch {
      setWarehousesData(undefined);
      setWarehousesFiltered(undefined);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const handleWarehouseSelection = (selectedWarehouse) => {
    if (selectedWarehouse) {
      const filtered = warehousesData?.Data?.filter(
        (x) => x.Id === selectedWarehouse.Id
      );
      setWarehousesFiltered(filtered);
      return;
    }

    setWarehousesFiltered(warehousesData?.Data);
  };

  useEffect(() => {
    if (tags) {
      setModel({
        ...model,
        Tags: [...tags],
      });
    }
  }, [tags]);

  useEffect(() => {
    if (model.Tags && model.Tags.length > 0) {
      setTags(JSON.parse(model.Tags));
    }

    getMeasurementUnits();
    getProviders();
    getInventoryMovementHeaders();
    if (!isWarehouseView) {
      getWarehouses();
    }
  }, []);

  useEffect(() => {
    if (!readOnly && model.Description) {
      setDescriptionCount(model.Description.length);
    }
  }, [model?.Description]);

  const handleChange = (e) => {
    if (readOnly) {
      return;
    }

    if (e.target.name === "NewTag") {
      setNewTag(e.target.value);
      return;
    }

    if (e.target.name === "Quantity") {
      if (e.target?.value?.length === 0) {
        setModel({
          ...model,
          [e.target.name]: 0,
        });
        return;
      }

      const inputValue = parseInt(e.target.value);
      setModel({
        ...model,
        [e.target.name]: inputValue > 0 ? inputValue : 0,
      });
      return;
    }

    setModel({
      ...model,
      [e.target.name]: e.target.value,
    });
  };

  const handleProviderChange = (selectedProvider) => {
    if (readOnly) {
      return;
    }

    if (!selectedProvider) {
      setModel({
        ...model,
        Provider: null,
        ProviderId: 0,
      });
      return;
    }

    setModel({
      ...model,
      Provider: selectedProvider,
      ProviderId: selectedProvider.Id,
    });
  };

  const handleUnitChange = (selectedUnit) => {
    if (readOnly) {
      return;
    }

    if (!selectedUnit) {
      setModel({
        ...model,
        Unit: null,
        UnitId: 0,
      });
      return;
    }

    setModel({
      ...model,
      Unit: selectedUnit,
      UnitId: selectedUnit.Id,
    });
  };

  const generateTagItem = (name) => ({
    FriendlyName: name,
    Name: Math.random().toString(),
  });

  const handleAddBtn = () => {
    setTags(
      !!tags ? [...tags, generateTagItem(newTag)] : [generateTagItem(newTag)]
    );

    setNewTag("");
  };

  const getComponents = () => {
    const result = [
      {
        Name: localize.general_information,
        Component: (
          <form className="w-full my-2">
            <div className="w-full flex flex-wrap mb-4">
              <FormControl required fullWidth>
                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  id="outlined-basic"
                  name="Code"
                  label={localize.code}
                  variant="outlined"
                  onChange={handleChange}
                  value={model.Code}
                />
              </FormControl>
            </div>

            <div className="w-full flex flex-wrap mb-4">
              <FormControl required fullWidth>
                <TextField
                  required
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  id="outlined-basic"
                  name="Name"
                  label={localize.name}
                  variant="outlined"
                  onChange={handleChange}
                  value={model.Name}
                />
              </FormControl>
            </div>

            <div className="w-full flex mb-4 gap-2 items-center">
              {model.Id > 0 && (
                <TextField
                  type="number"
                  disabled
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  value={model.Quantity}
                  onChange={handleChange}
                  size="small"
                  id="Quantity"
                  name="Quantity"
                  label={localize.quantity}
                  variant="outlined"
                  fullWidth
                />
              )}

              <Combobox
                isRequired
                id="Unit"
                handleValueChange={handleUnitChange}
                fieldValue={model?.Unit}
                fieldOptions={measurementUnitData?.Data ?? []}
                fieldLabel={localize.measurement_unit}
                customOptionLabel={(option) =>
                  option?.Symbol && option?.Symbol !== ""
                    ? `${option?.Name} (${option.Symbol})`
                    : option?.Name
                }
              />
            </div>

            <div className="w-full flex flex-wrap mb-4">
              <Combobox
                id="Provider"
                handleValueChange={handleProviderChange}
                fieldValue={model?.Provider}
                fieldOptions={providersData?.Data ?? []}
                fieldLabel={localize.provider}
                customOptionLabel={(option) =>
                  option?.Identification && option?.Identification !== ""
                    ? `${option?.Identification} - ${option.Name}`
                    : option?.Name
                }
              />
            </div>

            <div className="w-full flex flex-wrap mb-4">
              <FormControl required fullWidth>
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={{
                    style: {
                      fontSize: "0.8em",
                    },
                    maxLength: maximumCharactersForDescription,
                  }}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  id="outlined-basic"
                  name="Description"
                  label={localize.description}
                  variant="outlined"
                  onChange={handleChange}
                  value={model.Description}
                  helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
                />
              </FormControl>
            </div>
          </form>
        ),
      },
      {
        Name: localize.tags,
        Component: (
          <Box sx={{ height: "300px" }} className="flex flex-col gap-2 my-2">
            <Box className="flex gap-2 items-center">
              {!readOnly && (
                <TextField
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  size="small"
                  fullWidth
                  id="outlined-basic"
                  name="NewTag"
                  label={localize.new_tag}
                  variant="outlined"
                  onChange={handleChange}
                  value={newTag}
                  onKeyUp={(event) => {
                    if (event.key === "Enter") {
                      handleAddBtn();
                    }
                  }}
                />
              )}

              {!readOnly && (
                <Button
                  style={{ width: "150px" }}
                  disabled={!newTag || newTag.replaceAll(" ", "").length === 0}
                  size="small"
                  variant="contained"
                  color="success"
                  className="app_general_button_data app_success_button btn-sm"
                  onClick={handleAddBtn}
                >
                  <span className="mr-2">{localize.add}</span>
                  <Add />
                </Button>
              )}
            </Box>
            <Box
              sx={{
                overflowY: "auto",
                height: "100%",
                border: "1px solid gray",
                borderRadius: "4px",
              }}
            >
              <FilterBox
                filterData={tags}
                setFilterData={setTags}
                defaultFiltering={[]}
              />
            </Box>
          </Box>
        ),
      },
    ];

    if (model?.Id > 0) {
      result.push({
        Name: localize.inventory_movement_title,
        Component: loadingInventoryMovements ? (
          <LoadingPanel donotUseHscreen />
        ) : inventoryMovementHeaderData?.Data?.length > 0 ? (
          <Box className="flex flex-col gap-2">
            <InventoryMovementFilter
              fullData={inventoryMovementHeaderData?.Data}
              setDataFiltered={setInventoryMovementHeaderFiltered}
            />
            <List className="mt-4 flex flex-col gap-2">
              {inventoryMovementHeaderFiltered &&
              inventoryMovementHeaderFiltered.length > 0 ? (
                inventoryMovementHeaderFiltered?.map((item) => (
                  <InventoryMovementItem
                    key={`IM_${item.Id}`}
                    item={item}
                    showQuantity
                    onOpen={() => {
                      setInventoryMovementHeaderModel(item);
                      setShowDetailModal(true);
                    }}
                  />
                ))
              ) : (
                <Box className="w-full flex flex-col mt-4">
                  <Alert severity="info" sx={{ width: "100%" }}>
                    <label className="text-xs">
                      {localize.no_inventory_movements_for_supply}
                    </label>
                  </Alert>
                </Box>
              )}
            </List>
          </Box>
        ) : (
          <Box className="w-full flex flex-col mt-4">
            <Alert severity="info" sx={{ width: "100%" }}>
              <label className="text-xs">
                {localize.no_inventory_movements_for_supply}
              </label>
            </Alert>
          </Box>
        ),
      });

      if (!isWarehouseView) {
        result.push({
          Name: localize.warehouses,
          Component: loadingWarehouses ? (
            <LoadingPanel donotUseHscreen />
          ) : warehousesFiltered && warehousesFiltered.length > 0 ? (
            <Box className="flex flex-col gap-2 mt-3">
              <Combobox
                id="WarehouseFilter"
                handleValueChange={handleWarehouseSelection}
                fieldValue={warehouseFilter}
                fieldOptions={warehousesData?.Data ?? []}
                fieldLabel={localize.warehouse}
              />
              <Divider />
              <List className="flex flex-col gap-2 -w-full">
                {warehousesFiltered.map((item) => (
                  <EvidenceTempleateItem
                    key={`wh_${item.Id}`}
                    evidence={item}
                    customValue={item.Quantity}
                  />
                ))}
              </List>
            </Box>
          ) : (
            <Box sx={{ height: 340 }} className="w-full flex flex-col mt-4">
              <Alert severity="info" sx={{ width: "100%" }}>
                <label className="text-xs">
                  {localize.no_warehouses_for_supply_item}
                </label>
              </Alert>
            </Box>
          ),
        });
      }
    }

    return result;
  };

  return (
    <div style={{ width: "100%" }}>
      {showDetailModal && (
        <ModalBackground
          showModal={showDetailModal}
          setShowModal={setShowDetailModal}
          title={`${localize.movement} #${inventoryMovementHeaderModel.Id}`}
          hideSubmitButton
          onClickToCancel={() => {
            setShowDetailModal(false);
            setInventoryMovementHeaderModel(getClearInventoryMovement());
          }}
          cancelButtonText={localize.close}
          Component={
            <AddInventoryMovement
              model={inventoryMovementHeaderModel}
              setModel={setInventoryMovementHeaderModel}
            />
          }
        />
      )}

      {isWarehouseView && (
        <Box className="w-full mb-2">
          <Alert sx={{ width: "100%" }} severity="info">
            <label className="text-xs">
              <b>{localize.warehouse}: </b>
              {warehouse.Name}
            </label>
          </Alert>
        </Box>
      )}

      <ExpandableView components={getComponents()} />

      <CreatedByText entity={model} />
    </div>
  );
}
